import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useHasRole } from "@/hooks/useUserRoles";

export interface ExplainerFlags {
  site_tour_completed_core: boolean;
  site_tour_completed_role: boolean;
  mentor_explainer_seen_on_login: boolean;
  mentor_explainer_seen_on_request: boolean;
  mentor_explainer_dismissed_forever: boolean;
  entrepreneur_explainer_seen_on_login: boolean;
  entrepreneur_explainer_seen_on_request: boolean;
  entrepreneur_explainer_dismissed_forever: boolean;
}

// Hook to get global toggle settings
export function useGlobalExplainerToggles() {
  return useQuery({
    queryKey: ["global_explainer_toggles"],
    queryFn: async () => {
      const keys = [
        "force_mentor_explainer",
        "force_entrepreneur_explainer",
        "force_site_tour_core",
        "force_site_tour_role",
      ];
      
      const results: Record<string, boolean> = {};
      for (const key of keys) {
        const { data } = await supabase.rpc("get_site_setting", { setting_key: key });
        results[key] = data === "true";
      }
      return results;
    },
    staleTime: 30000, // Cache for 30 seconds
  });
}

// Hook to check if mentor explainer should show
export function useShouldShowMentorExplainer(trigger: "login" | "request") {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: globalToggles } = useGlobalExplainerToggles();
  const isMentor = profile?.role === "mentor";

  if (!user || !profile || !isMentor) return false;

  // Cast profile to include the new fields
  const flags = profile as unknown as ExplainerFlags;
  
  // Never show if dismissed forever
  if (flags.mentor_explainer_dismissed_forever) return false;

  // Check global force toggle (overrides seen flags)
  if (globalToggles?.force_mentor_explainer) return true;

  // Check specific trigger
  if (trigger === "login") {
    return !flags.mentor_explainer_seen_on_login;
  } else {
    return !flags.mentor_explainer_seen_on_request;
  }
}

// Hook to check if entrepreneur explainer should show
export function useShouldShowEntrepreneurExplainer(trigger: "login" | "request") {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: globalToggles } = useGlobalExplainerToggles();
  const isEntrepreneur = profile?.role === "entrepreneur";

  if (!user || !profile || !isEntrepreneur) return false;

  // Cast profile to include the new fields
  const flags = profile as unknown as ExplainerFlags;
  
  // Never show if dismissed forever
  if (flags.entrepreneur_explainer_dismissed_forever) return false;

  // Check global force toggle
  if (globalToggles?.force_entrepreneur_explainer) return true;

  // Check specific trigger
  if (trigger === "login") {
    return !flags.entrepreneur_explainer_seen_on_login;
  } else {
    return !flags.entrepreneur_explainer_seen_on_request;
  }
}

// Hook for managing explainer mutations
export function useExplainerMutations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const updateExplainerFlag = useMutation({
    mutationFn: async (updates: Partial<ExplainerFlags>) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update(updates as Record<string, boolean>)
        .eq("id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const markMentorExplainerSeen = (trigger: "login" | "request", dismissForever = false) => {
    const updates: Partial<ExplainerFlags> = {};
    if (trigger === "login") {
      updates.mentor_explainer_seen_on_login = true;
    } else {
      updates.mentor_explainer_seen_on_request = true;
    }
    if (dismissForever) {
      updates.mentor_explainer_dismissed_forever = true;
    }
    return updateExplainerFlag.mutateAsync(updates);
  };

  const markEntrepreneurExplainerSeen = (trigger: "login" | "request", dismissForever = false) => {
    const updates: Partial<ExplainerFlags> = {};
    if (trigger === "login") {
      updates.entrepreneur_explainer_seen_on_login = true;
    } else {
      updates.entrepreneur_explainer_seen_on_request = true;
    }
    if (dismissForever) {
      updates.entrepreneur_explainer_dismissed_forever = true;
    }
    return updateExplainerFlag.mutateAsync(updates);
  };

  const markTourComplete = (phase: "core" | "role") => {
    const updates: Partial<ExplainerFlags> = {};
    if (phase === "core") {
      updates.site_tour_completed_core = true;
    } else {
      updates.site_tour_completed_role = true;
    }
    return updateExplainerFlag.mutateAsync(updates);
  };

  return {
    markMentorExplainerSeen,
    markEntrepreneurExplainerSeen,
    markTourComplete,
    isUpdating: updateExplainerFlag.isPending,
  };
}

// Admin hook for managing explainers
export function useAdminExplainerManagement() {
  const queryClient = useQueryClient();
  const isAdmin = useHasRole("admin");

  const resetUserFlags = useMutation({
    mutationFn: async ({ userId, resetType }: { userId: string; resetType: "mentor" | "entrepreneur" | "tour" | "all" }) => {
      const { error } = await supabase.rpc("admin_reset_explainer_flags", {
        target_user_id: userId,
        reset_type: resetType,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const updateGlobalToggle = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: boolean }) => {
      const { error } = await supabase.rpc("admin_update_site_setting", {
        setting_key: key,
        setting_value: value.toString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["global_explainer_toggles"] });
    },
  });

  return {
    resetUserFlags,
    updateGlobalToggle,
    isAdmin,
  };
}
